// Obecność kierowców: kto jest teraz w aplikacji (ranking kierowców + pulpit admina).
// Aplikacja kierowcy wysyła mały sygnał co ~25 s, dopóki jest otwarta na pierwszym planie (/call-api/presence).
// Błędy sieci są ciche — brak sygnału to po prostu "nie wiadomo", nigdy komunikat dla kierowcy.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { sameName, initials } from "./shared.jsx";

const BEAT_MS = 25000;
const POLL_MS = 15000;

async function fetchJson(url, opts, ms) {
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), ms || 8000) : null;
  try {
    const res = await fetch(url, { cache: "no-store", ...(opts || {}), signal: ctl ? ctl.signal : undefined });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function makeApi(list) {
  const online = (list || []).filter((p) => p.online);
  return {
    list: list || [],
    online,
    onlineNames: online.map((p) => p.name),
    count: online.length,
    isOnline: (name) => online.some((p) => sameName(p.name, name)),
  };
}

// Kierowca: wysyła sygnały (beat = true) i dostaje listę obecnych. Podgląd admina "jako kierowca" (beat = false) tylko czyta.
export function useDriverPresence(name, beat) {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!name) return undefined;
    let stopped = false;
    const visible = () => typeof document === "undefined" || document.visibilityState !== "hidden";
    const tick = async (off) => {
      if (stopped) return;
      let data;
      if (beat) data = await fetchJson("/call-api/presence", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, off: !!off }) });
      else data = await fetchJson("/call-api/presence");
      if (!stopped && data && Array.isArray(data.presence)) setList(data.presence);
    };
    const leave = () => {
      if (!beat) return;
      try {
        const body = JSON.stringify({ name, off: true });
        if (navigator.sendBeacon && navigator.sendBeacon("/call-api/presence", new Blob([body], { type: "application/json" }))) return;
        fetch("/call-api/presence", { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
      } catch (e) {
        /* wyjście z aplikacji — i tak wygaśnie po ~70 s */
      }
    };
    const first = setTimeout(() => tick(false), 1500); // nie konkuruje ze startem aplikacji
    const iv = setInterval(() => {
      if (visible()) tick(false);
    }, BEAT_MS);
    const onVis = () => {
      if (visible()) tick(false);
      else leave();
    };
    const onOnline = () => tick(false);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", leave);
    window.addEventListener("online", onOnline);
    return () => {
      stopped = true;
      clearTimeout(first);
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("online", onOnline);
      leave();
    };
  }, [name, beat]);
  return useMemo(() => {
    const api = makeApi(list);
    if (!beat || !name) return api;
    // ja sam jestem online, dopóki aplikacja jest otwarta (zanim wróci pierwsza odpowiedź serwera)
    const me = api.isOnline(name);
    if (me) return api;
    const withMe = [...api.list.filter((p) => !sameName(p.name, name)), { name, online: true, ago: 0 }];
    return makeApi(withMe);
  }, [list, name, beat]);
}

// Admin: tylko odczyt, gdy zakładka jest widoczna.
export function useAdminPresence(active) {
  const [state, setState] = useState({ list: [], at: 0 });
  useEffect(() => {
    if (!active) return undefined;
    let stopped = false;
    const go = async () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const data = await fetchJson("/call-api/presence");
      if (!stopped && data && Array.isArray(data.presence)) setState({ list: data.presence, at: Date.now() });
    };
    go();
    const iv = setInterval(go, POLL_MS);
    const onVis = () => go();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [active]);
  return state;
}

function formatAgo(ms) {
  const m = Math.floor(ms / 60000);
  if (m < 1) return "przed chwilą";
  if (m < 60) return `${m} min temu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} godz. temu`;
  const d = Math.floor(h / 24);
  return d === 1 ? "wczoraj" : `${d} dni temu`;
}

function Face({ name, avatars }) {
  return <>{(avatars && avatars[name]) || initials(name)}</>;
}

// Pigułka nad rankingiem: "● 3 online" + nakładające się awatary
export function OnlinePill({ presence, avatars }) {
  const names = presence ? presence.onlineNames : [];
  if (!names.length) return null;
  const shown = names.slice(0, 5);
  return (
    <div className="pr-pill" key={names.length} aria-live="polite">
      <span className="pr-live" />
      <span className="pr-pill-faces">
        {shown.map((n, i) => (
          <span key={n} className="pr-pill-face" style={{ zIndex: 10 - i, animationDelay: `${i * 70}ms` }}>
            <Face name={n} avatars={avatars} />
          </span>
        ))}
        {names.length > shown.length && <span className="pr-pill-face pr-pill-more">+{names.length - shown.length}</span>}
      </span>
      <span className="pr-pill-text">
        <b>{names.length}</b> {names.length === 1 ? "kierowca online" : "online teraz"}
      </span>
    </div>
  );
}

export function OnlineDot() {
  return <i className="pr-dot" title="Online" />;
}

// Karta na pulpicie admina
export function AdminOnlineCard({ drivers, avatars, presence }) {
  const [all, setAll] = useState(false);
  const [, force] = useState(0);
  const tRef = useRef(null);
  useEffect(() => {
    tRef.current = setInterval(() => force((x) => x + 1), 30000);
    return () => clearInterval(tRef.current);
  }, []);
  const list = presence ? presence.list : [];
  const at = presence ? presence.at : 0;
  const rows = useMemo(() => {
    const now = Date.now();
    const seen = (name) => list.find((p) => sameName(p.name, name));
    const out = (drivers || [])
      .filter((d) => d && d.name)
      .map((d) => {
        const p = seen(d.name);
        const ago = p ? p.ago + (at ? Math.max(0, now - at) : 0) : null;
        return { name: d.name, online: !!(p && p.online), ago };
      });
    out.sort((a, b) => (a.online === b.online ? (a.ago == null) - (b.ago == null) || (a.ago || 0) - (b.ago || 0) : a.online ? -1 : 1));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drivers, list, at, Math.floor(Date.now() / 30000)]);
  const onlineRows = rows.filter((r) => r.online);
  const offRows = rows.filter((r) => !r.online);
  const shownOff = all ? offRows : offRows.slice(0, 6);
  const loaded = at > 0;
  return (
    <div className="pr-card fade-up">
      <div className="pr-card-glow" />
      <div className="pr-card-head">
        <span className="pr-live pr-live-lg" />
        <div className="pr-card-title">
          Na żywo w aplikacji
          <small>{!loaded ? "sprawdzam…" : onlineRows.length ? "kierowcy, którzy mają teraz otwartą appkę" : "nikt nie ma teraz otwartej appki"}</small>
        </div>
        <div className="pr-card-count" key={onlineRows.length}>
          {onlineRows.length}
          <span>/ {rows.length}</span>
        </div>
      </div>
      {onlineRows.length > 0 && (
        <div className="pr-tiles">
          {onlineRows.map((r, i) => (
            <div key={r.name} className="pr-tile on" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="pr-tile-av">
                <span className="pr-ripple" />
                <span className="pr-ripple pr-ripple-2" />
                <div className="pr-tile-face">
                  <Face name={r.name} avatars={avatars} />
                </div>
                <i className="pr-dot" />
              </div>
              <div className="pr-tile-name">{r.name}</div>
              <div className="pr-tile-sub on">online teraz</div>
            </div>
          ))}
        </div>
      )}
      {shownOff.length > 0 && (
        <>
          {onlineRows.length > 0 && <div className="pr-sep">Poza aplikacją</div>}
          <div className="pr-tiles pr-tiles-off">
            {shownOff.map((r) => (
              <div key={r.name} className="pr-tile off">
                <div className="pr-tile-av">
                  <div className="pr-tile-face">
                    <Face name={r.name} avatars={avatars} />
                  </div>
                </div>
                <div className="pr-tile-name">{r.name}</div>
                <div className="pr-tile-sub">{r.ago == null ? "brak danych" : formatAgo(r.ago)}</div>
              </div>
            ))}
          </div>
          {offRows.length > 6 && (
            <button className="pr-more" onClick={() => setAll((v) => !v)}>
              {all ? "Pokaż mniej" : `Pokaż pozostałych (${offRows.length - 6})`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
